import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notification } from 'antd';
import { subscriptionApi } from '../api/subscription';
import { ActiveCard, EarlyPaymentOutcome, EarlyPaymentResult, PayEarlyResult, PaymentHistoryItem, PaymentOutcome, SubscriptionInfo, SubscriptionStatus, UnsubscribeResult } from '../types/subscription';
import { useTelegram } from './TelegramContext';
import { useLoading } from './LoadingContext';
import { formatDateTime } from '../utils/format';

export interface OnboardingContextValue {
    isInitializing: boolean;
    initialError: string;
    subscriptionInfo: SubscriptionInfo | null;
    subscriptionStatus: SubscriptionStatus | null;
    activeCard: ActiveCard | null;
    history: PaymentHistoryItem[];
    isHistoryLoading: boolean;
    isUnsubscribing: boolean;
    unsubscribeResult: UnsubscribeResult | null;
    /** Invoice id of an early payment whose poll is still running, so a re-mounted page shows it processing. */
    pendingEarlyInvoiceId: string | null;
    reloadDashboard: () => Promise<void>;
    refreshStatus: () => Promise<void>;
    waitForActiveSubscription: () => Promise<PaymentOutcome>;
    loadHistory: () => Promise<void>;
    unsubscribe: () => Promise<UnsubscribeResult>;
    payEarly: (invoiceId: string) => Promise<PayEarlyResult>;
    waitForEarlyPayment: (invoiceId: string) => Promise<EarlyPaymentResult>;
}

// Activating a card only queues a `Pay` job on the backend (card charge -> `Accept` job ->
// subscriber flips active), so the HTTP response lands well before the subscription exists.
// Poll `/api/status` until it catches up rather than trusting a single read.
const ACTIVATION_POLL_INTERVAL_MS = 2000;
const ACTIVATION_POLL_BUDGET_MS = 60000;

export const OnboardingContext = createContext<OnboardingContextValue>({
    isInitializing: true,
    initialError: '',
    subscriptionInfo: null,
    subscriptionStatus: null,
    activeCard: null,
    history: [],
    isHistoryLoading: false,
    isUnsubscribing: false,
    unsubscribeResult: null,
    pendingEarlyInvoiceId: null,
    reloadDashboard: async () => undefined,
    refreshStatus: async () => undefined,
    waitForActiveSubscription: async () => 'pending',
    loadHistory: async () => undefined,
    unsubscribe: async () => ({
        unsubscribed: false,
        next_payment_cancelled: false,
        is_active: false,
        access_until: '',
    }),
    payEarly: async () => ({
        started: false,
        invoice_id: '',
        schedule_id: 0,
        pay_time: '',
    }),
    waitForEarlyPayment: async () => ({
        outcome: 'pending',
        endTime: null,
        payTime: null,
    }),
});

export const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
    const { isAuthorized, isAuthorizing, authError, webApp } = useTelegram();
    const { setIsFullScreenLoading } = useLoading();
    const [isInitializing, setIsInitializing] = useState(true);
    const [initialError, setInitialError] = useState('');
    const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [activeCard, setActiveCard] = useState<ActiveCard | null>(null);
    const [history, setHistory] = useState<PaymentHistoryItem[]>([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isUnsubscribing, setIsUnsubscribing] = useState(false);
    const [unsubscribeResult, setUnsubscribeResult] = useState<UnsubscribeResult | null>(null);
    const [pendingEarlyInvoiceId, setPendingEarlyInvoiceId] = useState<string | null>(null);

    const reloadDashboard = useCallback(async () => {
        setIsInitializing(true);
        setInitialError('');
        setIsFullScreenLoading(true);

        try {
            const [info, status, card, payments] = await Promise.all([
                subscriptionApi.getSubscriptionInfo(),
                subscriptionApi.getSubscriptionStatus(),
                subscriptionApi.getActiveCard(),
                subscriptionApi.getPaymentHistory(),
            ]);

            setSubscriptionInfo(info);
            setSubscriptionStatus(status);
            setActiveCard(card || null);
            setHistory(payments);
        } catch (error) {
            setInitialError('Ma\'lumotlarni yuklab bo\'lmadi');
            throw error;
        } finally {
            setIsInitializing(false);
            setIsFullScreenLoading(false);
        }
    }, [setIsFullScreenLoading]);

    /**
     * Refetch the parts that go stale while the app is open, without the fullscreen spinner or the
     * `isInitializing` flip that `reloadDashboard` performs — this runs on screen mount, where a
     * blocking overlay would flash on every navigation.
     */
    const refreshStatus = useCallback(async () => {
        const [status, card] = await Promise.all([
            subscriptionApi.getSubscriptionStatus(),
            subscriptionApi.getActiveCard(),
        ]);

        setSubscriptionStatus(status);
        setActiveCard(card || null);
    }, []);

    const waitForActiveSubscription = useCallback(async (): Promise<PaymentOutcome> => {
        setIsFullScreenLoading(true);

        let outcome: PaymentOutcome = 'pending';
        const deadline = Date.now() + ACTIVATION_POLL_BUDGET_MS;

        try {
            while (Date.now() < deadline) {
                try {
                    const status = await subscriptionApi.getSubscriptionStatus();

                    if (status.is_active) {
                        outcome = 'active';
                        break;
                    }

                    // Nothing left in flight and still not active: the charge is over and it did
                    // not result in a subscription.
                    if (!status.pending_payment) {
                        outcome = 'failed';
                        break;
                    }
                } catch {
                    // Transient errors while polling are ignored; the reloadDashboard below
                    // surfaces a persistent failure instead.
                }

                await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_INTERVAL_MS));
            }
        } finally {
            // reloadDashboard owns the spinner from here, and clears it even if it throws.
            await reloadDashboard().catch(() => undefined);
        }

        return outcome;
    }, [reloadDashboard, setIsFullScreenLoading]);

    const loadHistory = useCallback(async () => {
        setIsHistoryLoading(true);
        try {
            const items = await subscriptionApi.getPaymentHistory();
            setHistory(items);
        } finally {
            setIsHistoryLoading(false);
        }
    }, []);

    const unsubscribe = useCallback(async () => {
        setIsUnsubscribing(true);
        try {
            const result = await subscriptionApi.unsubscribe();
            setUnsubscribeResult(result);
            return result;
        } finally {
            setIsUnsubscribing(false);
        }
    }, []);

    const payEarly = useCallback((invoiceId: string): Promise<PayEarlyResult> => {
        return subscriptionApi.payEarly(invoiceId);
    }, []);

    /**
     * Announces the settled early-payment outcome. Lives in the context because the poll can outlive
     * the page that started it; uses the static `notification` API (as `utils/axios.ts` does) for the
     * same reason. Haptics only for the two clear-cut outcomes.
     */
    const notifyEarlyOutcome = useCallback((outcome: EarlyPaymentOutcome, endTime: string | null, payTime: string | null) => {
        if (outcome === 'active') {
            notification.success({
                message: "To'lov qabul qilindi",
                description: endTime ? `Obuna tugash sanasi: ${formatDateTime(endTime)}` : undefined,
                placement: 'bottom',
            });
            webApp?.HapticFeedback?.notificationOccurred?.('success');
            return;
        }

        if (outcome === 'failed') {
            notification.warning({
                message: "To'lov amalga oshmadi",
                description: `Obunangiz saqlanib qoldi, kanaldan chiqarilmaysiz. To'lov ${formatDateTime(payTime)} da avtomatik qayta uriniladi. Sabab bot orqali yuborildi.`,
                placement: 'bottom',
            });
            webApp?.HapticFeedback?.notificationOccurred?.('error');
            return;
        }

        if (outcome === 'cancelled') {
            notification.info({
                message: "To'lov amalga oshirilmadi",
                description: "Obuna bekor qilingani sababli to'lov amalga oshirilmadi.",
                placement: 'bottom',
            });
            return;
        }

        // pending — the poll budget ran out with the charge still in flight.
        notification.info({
            message: "To'lov tasdiqlanmoqda",
            description: 'Natija bot orqali yuboriladi.',
            placement: 'bottom',
        });
    }, [webApp]);

    /**
     * Polls the payment history until the early charge on `invoiceId` settles, updating `history` on
     * each poll so the row's `in_process` keeps the UI in its processing state. No fullscreen spinner —
     * unlike a first payment, the subscriber already has a working subscription and the page has its
     * own content.
     */
    const waitForEarlyPayment = useCallback(async (invoiceId: string): Promise<EarlyPaymentResult> => {
        setPendingEarlyInvoiceId(invoiceId);

        let outcome: EarlyPaymentOutcome = 'pending';
        let endTime: string | null = null;
        let payTime: string | null = null;
        const deadline = Date.now() + ACTIVATION_POLL_BUDGET_MS;

        try {
            while (Date.now() < deadline) {
                try {
                    const items = await subscriptionApi.getPaymentHistory();
                    setHistory(items);

                    const item = items.find((it) => it.id === invoiceId);

                    if (item) {
                        if (item.is_paid) {
                            outcome = 'active';
                            break;
                        }

                        // An unsubscribe raced the charge: `Job\Pay` skipped it and the schedule is gone.
                        if (item.status === 'cancelled') {
                            outcome = 'cancelled';
                            break;
                        }

                        // The charge failed and the manual `Reject` released the claim; the schedule and
                        // its `pay_time` are kept, so the cron will retry.
                        if (item.status === 'pending' && !item.in_process) {
                            outcome = 'failed';
                            break;
                        }
                    }
                    // Still in flight (or missing from this poll): keep waiting.
                } catch {
                    // Transient poll errors are ignored; the refresh below surfaces a real failure.
                }

                await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_INTERVAL_MS));
            }
        } finally {
            // Read fresh — the poll can outlive several state updates, so the `subscriptionStatus` in
            // this closure is stale. These also refresh the page's own state.
            try {
                const [status, card, items] = await Promise.all([
                    subscriptionApi.getSubscriptionStatus(),
                    subscriptionApi.getActiveCard(),
                    subscriptionApi.getPaymentHistory(),
                ]);

                setSubscriptionStatus(status);
                setActiveCard(card || null);
                setHistory(items);

                endTime = status.end_time ?? null;
                payTime = status.next_payment?.pay_time ?? null;
            } catch {
                // Leave the last polled state in place if the final refresh fails.
            }

            setPendingEarlyInvoiceId(null);
            notifyEarlyOutcome(outcome, endTime, payTime);
        }

        return { outcome, endTime, payTime };
    }, [notifyEarlyOutcome]);

    useEffect(() => {
        if (isAuthorizing) {
            return;
        }

        if (!isAuthorized) {
            setIsInitializing(false);
            setInitialError(authError || 'Avtorizatsiya muvaffaqiyatsiz tugadi');
            return;
        }

        reloadDashboard().catch(() => undefined);
    }, [isAuthorized, isAuthorizing, authError, reloadDashboard]);

    const value = useMemo(
        () => ({
            isInitializing,
            initialError,
            subscriptionInfo,
            subscriptionStatus,
            activeCard,
            history,
            isHistoryLoading,
            isUnsubscribing,
            unsubscribeResult,
            pendingEarlyInvoiceId,
            reloadDashboard,
            refreshStatus,
            waitForActiveSubscription,
            loadHistory,
            unsubscribe,
            payEarly,
            waitForEarlyPayment,
        }),
        [
            isInitializing,
            initialError,
            subscriptionInfo,
            subscriptionStatus,
            activeCard,
            history,
            isHistoryLoading,
            isUnsubscribing,
            unsubscribeResult,
            pendingEarlyInvoiceId,
            reloadDashboard,
            refreshStatus,
            waitForActiveSubscription,
            loadHistory,
            unsubscribe,
            payEarly,
            waitForEarlyPayment,
        ]
    );

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = () => useContext(OnboardingContext);
